import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Mail,
  Phone,
  Globe,
  Building2,
  Users,
  Linkedin,
  Copy,
  Download,
  Search,
  CheckCircle2,
  Shield,
  Filter,
  Trash2,
  ChevronLeft,
  ChevronRight,
  BookOpen
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = {
  'hunter.io': { label: 'Hunter', color: 'bg-orange-100 text-orange-700' },
  'website': { label: 'Sito Web', color: 'bg-blue-100 text-blue-700' },
  'paginegialle': { label: 'PagineGialle', color: 'bg-yellow-100 text-yellow-700' },
  'google_maps': { label: 'Maps', color: 'bg-emerald-100 text-emerald-700' },
  'batch': { label: 'Batch', color: 'bg-purple-100 text-purple-700' },
  'email_lookup': { label: 'Lookup', color: 'bg-rose-100 text-rose-700' },
  'company_batch': { label: 'Aziende', color: 'bg-indigo-100 text-indigo-700' },
};

export default function EmailDirectoryPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const fetchDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (sourceFilter) params.append('source', sourceFilter);
      if (verifiedOnly) params.append('verified_only', 'true');
      if (searchQuery) params.append('search', searchQuery);

      const res = await axios.get(`${API_URL}/email-directory?${params}`);
      setContacts(res.data.contacts);
      setTotal(res.data.total);
      setTotalPages(res.data.pages);
      setStats(res.data.stats);
    } catch {
      toast.error('Errore caricamento rubrica');
    } finally {
      setLoading(false);
    }
  }, [page, sourceFilter, verifiedOnly, searchQuery]);

  useEffect(() => { fetchDirectory(); }, [fetchDirectory]);

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (sourceFilter) params.append('source', sourceFilter);
      if (verifiedOnly) params.append('verified_only', 'true');

      const res = await axios.get(`${API_URL}/email-directory/export/csv?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rubrica_email_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success('CSV scaricato!');
    } catch {
      toast.error('Errore export');
    }
  };

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API_URL}/email-directory/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      setTotal(prev => prev - 1);
      toast.success('Contatto eliminato');
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato!');
  };

  const copyAllEmails = () => {
    const emails = contacts.map(c => c.email).join('\n');
    navigator.clipboard.writeText(emails);
    toast.success(`${contacts.length} email copiate!`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />

      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-600 text-white flex items-center justify-center">
                <BookOpen className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Rubrica Email</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="directory-stats">
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  <p className="text-xs text-slate-500">Email Totali</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.verified}</p>
                  <p className="text-xs text-slate-500">Verificate</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.sources?.['hunter.io'] || 0}</p>
                  <p className="text-xs text-slate-500">Hunter.io</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.sources?.website || 0}</p>
                  <p className="text-xs text-slate-500">Da Siti Web</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters & Actions */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex flex-wrap gap-2 items-center">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Cerca email, azienda, nome..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    className="pl-9 w-64"
                    data-testid="search-input"
                  />
                </div>
                <select
                  value={sourceFilter}
                  onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
                  className="h-10 px-3 rounded-lg border border-slate-300 text-sm"
                  data-testid="source-filter"
                >
                  <option value="">Tutte le fonti</option>
                  <option value="hunter.io">Hunter.io</option>
                  <option value="website">Sito Web</option>
                  <option value="paginegialle">PagineGialle</option>
                  <option value="google_maps">Google Maps</option>
                </select>
                <button
                  onClick={() => { setVerifiedOnly(!verifiedOnly); setPage(1); }}
                  className={`flex items-center gap-1.5 h-10 px-3 rounded-lg border-2 text-sm transition ${
                    verifiedOnly ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500'
                  }`}
                  data-testid="verified-filter"
                >
                  <CheckCircle2 className="w-4 h-4" /> Solo verificate
                </button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyAllEmails} data-testid="copy-all-btn">
                  <Copy className="w-4 h-4 mr-1" /> Copia tutte
                </Button>
                <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-1" /> Esporta CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email table */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : contacts.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">Rubrica vuota</h3>
              <p className="text-slate-500 mb-4">Le email verranno salvate automaticamente quando esegui ricerche</p>
              <Button onClick={() => navigate('/batch-search')} className="bg-teal-600 hover:bg-teal-700 text-white">
                Vai alla Ricerca Batch
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="email-directory-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Nome</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Azienda</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Ruolo</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Stato</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Fonte</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => {
                    const srcCfg = SOURCE_LABELS[c.source] || { label: c.source, color: 'bg-slate-100 text-slate-600' };
                    const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                    return (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`contact-row-${c.id}`}>
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm text-slate-900">{c.email}</span>
                        </td>
                        <td className="py-3 px-4 text-slate-700">{fullName || '-'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1 text-slate-600">
                            {c.company && <><Building2 className="w-3 h-3" /> {c.company}</>}
                            {!c.company && '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600 text-xs">{c.position || '-'}</td>
                        <td className="py-3 px-4">
                          {c.verified ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Verificata
                            </Badge>
                          ) : c.confidence ? (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <Shield className="w-3 h-3 mr-1" /> {c.confidence}%
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`text-xs ${srcCfg.color}`}>{srcCfg.label}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(c.email)} title="Copia">
                              <Copy className="w-3 h-3" />
                            </Button>
                            {c.linkedin && (
                              <a href={c.linkedin} target="_blank" rel="noopener noreferrer">
                                <Button variant="ghost" size="sm" title="LinkedIn"><Linkedin className="w-3 h-3" /></Button>
                              </a>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => deleteContact(c.id)} title="Elimina"
                              className="text-red-400 hover:text-red-600 hover:bg-red-50">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-slate-200">
                <span className="text-sm text-slate-500">{total} contatti totali — Pagina {page}/{totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}
